// Ubicación en el repo: src/lib/print.js
// Utilidad de impresión — replica el patrón #printArea + printWithTitle()
// de prototipo-actual.html (líneas ~2138-2145): inyecta HTML en un
// contenedor oculto que solo se muestra durante window.print() (ver CSS
// @media print en index.css), en vez de abrir una ventana nueva (evita
// bloqueadores de pop-ups). El contenedor #print-area vive una sola vez
// en Layout.jsx.
//
// BUG encontrado al agregar el logo de sucursal (DECISIONS.md #39): esta
// función seteaba innerHTML y llamaba a window.print() en la misma
// pasada síncrona. Mientras el contenido venía todo embebido (texto,
// SVG inline) no importaba, pero el logo es una <img> con una signed URL
// de Storage — una petición de red — y el navegador imprimía antes de que
// esa imagen terminara de cargar, así que nunca aparecía. Ahora se espera
// a que todas las <img> del área de impresión carguen (o fallen, o pasen
// 4s de respaldo) antes de llamar a window.print().
//
// `fitOnePage` (22-sep-2026, pedido de Nicolás sobre el informe de
// audiometría: "siempre quede en una hoja"): opt-in a propósito, no se
// activa para el resto de los documentos (recibos, cotización, garantía,
// agenda del día, receta, consentimiento) que nunca se probaron ni se
// pidieron con esta garantía — algunos, como la agenda del día, pueden
// necesitar varias hojas de verdad si hay muchas citas ese día, y
// forzarlos a una sola hoja los rompería.
//
// BUG encontrado (reportado por Nicolás, mismo día): la primera versión de
// esto envolvía el HTML en un `transform:scale()` cuando no entraba en una
// hoja. Se veía bien en pantalla, pero al imprimir de verdad la firma
// seguía cayendo en la hoja 2. Causa real: `transform` en CSS solo afecta
// cómo se PINTA un elemento, no su tamaño de layout — el navegador decide
// DÓNDE cortar la página según el alto real (sin transformar) del
// contenido, así que un bloque que visualmente se ve achicado por
// `transform:scale()` sigue "ocupando" su alto original a efectos de dónde
// va el salto de página. Es una limitación conocida de los navegadores,
// no algo que dependiera de un valor mal calculado.
//
// Arreglo real: cuando el contenido no entra en una hoja, en vez de
// escalarlo con CSS se convierte a una sola imagen ya achicada al tamaño
// final (con `html2canvas`, la misma herramienta que ya arma el PDF
// adjunto del correo — ver pdfExport.js) y se imprime esa imagen. Una
// imagen no tiene puntos internos de corte de página, así que si se le da
// un tamaño que entra en la hoja, físicamente no puede partirse en dos.
// Cuando el contenido SÍ entra tal cual, se sigue imprimiendo el HTML
// normal (vector, con texto seleccionable) — el paso a imagen es sólo el
// respaldo para el caso en que no entra, no el camino por defecto.
const MM_TO_PX = 96 / 25.4;
const PAGE_MARGIN_MM = 10; // igual a `@page { margin: 10mm; }`
const LENS_MARGIN_MM = 8; // igual a `@page lensA5 { margin: 8mm; }`
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const A5_WIDTH_MM = 148;
const A5_HEIGHT_MM = 210;
const FIT_SAFETY = 0.98; // pequeño margen extra, por si el reflujo a un ancho distinto no da exacto

export async function printHTML(title, html, { lensA5 = false, fitOnePage = false } = {}) {
  const area = document.getElementById('print-area');
  if (!area) return;
  area.className = lensA5 ? 'lens-print' : '';

  let finalHTML = html;
  if (fitOnePage) {
    const marginMm = lensA5 ? LENS_MARGIN_MM : PAGE_MARGIN_MM;
    const contentWidthMm = (lensA5 ? A5_WIDTH_MM : A4_WIDTH_MM) - marginMm * 2;
    const contentHeightMm = (lensA5 ? A5_HEIGHT_MM : A4_HEIGHT_MM) - marginMm * 2;
    const contentWidthPx = Math.round(contentWidthMm * MM_TO_PX);

    const measurer = document.createElement('div');
    measurer.style.position = 'fixed';
    measurer.style.top = '0';
    measurer.style.left = '-10000px';
    measurer.style.width = `${contentWidthPx}px`;
    measurer.style.background = '#ffffff';
    measurer.innerHTML = html;
    document.body.appendChild(measurer);
    await waitForImages(measurer);
    const measuredHeightPx = measurer.scrollHeight;
    const measuredHeightMm = measuredHeightPx / MM_TO_PX;

    if (measuredHeightMm > contentHeightMm) {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(measurer, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const safeScale = (contentHeightMm / measuredHeightMm) * FIT_SAFETY;
      const imgWidthMm = contentWidthMm * safeScale;
      const imgHeightMm = measuredHeightMm * safeScale;
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      finalHTML = `<img src="${imgData}" style="width:${imgWidthMm}mm; height:${imgHeightMm}mm; display:block;" />`;
    }

    document.body.removeChild(measurer);
  }

  area.innerHTML = finalHTML;

  await waitForImages(area);

  const original = document.title;
  document.title = title;
  const restore = () => {
    document.title = original;
    window.removeEventListener('afterprint', restore);
  };
  window.addEventListener('afterprint', restore);
  window.print();
  setTimeout(restore, 3000); // respaldo por si el navegador no dispara afterprint
}

// Exportada (22-sep-2026, DECISIONS.md #119) para que pdfExport.js la
// reutilice al armar el PDF adjunto del correo de audiometría, en vez de
// duplicar la misma lógica de espera de imágenes.
export function waitForImages(container, timeoutMs = 4000) {
  const imgs = Array.from(container.querySelectorAll('img'));
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true }); // no bloquear la impresión si el logo no cargó
          setTimeout(done, timeoutMs);
        })
    )
  );
}

// Mismo escape básico que esc() del prototipo — el contenido viene de
// nuestra propia base, pero igual se sanea antes de inyectar como HTML.
export function esc(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
